import React, {
  useState,
  MouseEvent,
  useEffect,
} from 'react';
import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import Popover from '@mui/material/Popover';
import Button from 'pages/Dashboard/components/Button';
import {
  useGetMasterTemplateByName,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import {
  normalizeBillingCodes,
} from 'pages/Dashboard/pages/Billing/helpers';
import {
  TemplateListItemDTO,
  VisitNoteBillDTO,
} from 'dtos';
import TemplateSelect from 'pages/Dashboard/components/TemplateSelect';
import {
  useAppendToVisitNote,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  MasterTemplateIcon,
} from 'core/icons';

type BillingKey = 'assessmentCodes' | 'serviceCodes' | 'customServiceCodes';

type Props = {
  disabled?: boolean;
  billingKeysMapping?: Record<BillingKey, string>;
  buttonText?: string;
};

const defaultBillingKeysMapping: Record<BillingKey, string> = {
  assessmentCodes: 'bill.assessmentCodes',
  serviceCodes: 'bill.serviceCodes',
  customServiceCodes: 'bill.customServiceCodes',
};

export default function MasterTemplateButton({
  disabled,
  billingKeysMapping = defaultBillingKeysMapping,
  buttonText = 'Use Master Template',
}: Props) {
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateListItemDTO | null>(null);
  const { data: template } = useGetMasterTemplateByName(
    selectedTemplate?.templateName ?? undefined,
  );
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTemplateSelect = (template: TemplateListItemDTO) => {
    setSelectedTemplate(template);
    handleClose();
  };

  const { appendToTextFields, appendToBillFields } = useAppendToVisitNote();

  useEffect(() => {
    if (!isNil(template) && !isEmpty(template)) {
      const bill = {
        assessmentCodes: template?.assessmentCodes ?? [],
        serviceCodes: normalizeBillingCodes(
          template?.billingCodes ?? [],
          {},
          template?.assessmentCodes ?? [],
        ),
        customServiceCodes: (template?.customServiceCodes ?? []).map(
          ({ keyId, ...item }) => item,
        ),
      };

      appendToTextFields(template);
      appendToBillFields(bill as VisitNoteBillDTO);
      setSelectedTemplate(null);
    }
  }, [template]);

  return (
    <>
      <Button
        className={clsx('!px-2.5 !py-0.5', {
          '!border-secondary nw-use-master-template-button': !disabled,
        })}
        variant="outlined"
        color="primary"
        type="button"
        disabled={disabled}
        onClick={handleClick}
      >
        <MasterTemplateIcon className="!mr-1.5 !h-4 !w-4" />
        {buttonText}
      </Button>
      <Popover
        id="existing-template-menu"
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        slotProps={{
          paper: {
            className: '!overflow-visible !shadow-lg',
          },
        }}
        anchorOrigin={{
          vertical: (anchorEl?.offsetHeight ?? 0) + 4,
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <div className="w-80 relative">
          <TemplateSelect onChange={handleTemplateSelect} />
        </div>
      </Popover>
    </>
  );
}
